import React from 'react';
import PropTypes from 'prop-types';
import mergeWith from 'lodash/mergeWith';
import { Form, Row, Col } from 'react-bootstrap-5';

import HorizontalForm from 'components/form/horizontal_form';
import { defaultProperty, propertyTypeSubtypeMap, yesNoOptions, propertyUseIdOptions } from './assignment_constants';

export default class PropertyInfoSection extends React.Component {
  static propTypes = {
    handlePropertyTypeChange: PropTypes.func.isRequired,
    property: PropTypes.object,
    onChange: PropTypes.func.isRequired
  }

  state = {
    expanded: false
  }

  toggleExpand = () => {
    this.setState((prevState) => {
      return { expanded: !prevState.expanded };
    });
  }

  handlePropertyTypeIdChange = (event) => {
    const propertyTypeId = event.target.value;
    const changes = { propertyTypeId, propertySubtypeId: null };

    if (propertyTypeId != null) {
      const subtypes = this.getPropertySubtypes(propertyTypeId);
      if (subtypes.length > 0) {
        changes.propertySubtypeId = `${subtypes[0].id || ""}`;
      }
    }

    this.props.handlePropertyTypeChange(changes);
  };

  getPropertySubtypes = (propertyTypeId) => {
    const propertyType = propertyTypeSubtypeMap.find(type => type.id == propertyTypeId);
    return (propertyType || propertyTypeSubtypeMap[0] || {}).subtypes || [];
  }

  handleDateChange = (_date, event) => this.props.onChange(event)

  measurementLengthLabel = () => {
    const { property } = this.state;
    const isImperial = !property || property.imperial == null || property.imperial;
    return isImperial ? "ft" : "m";
  }

  renderAdditionalFields() {
    if (!this.state.expanded) return;

    const property = mergeWith(defaultProperty, this.props.property);

    return (
      <div className="form-vertical-field-container my-4">
        <HorizontalForm.TextInput
          label={`Lot Frontage (${this.measurementLengthLabel()}`}
          name='frontage'
          number
          value={property.frontage}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          label={`Lot Depth (${this.measurementLengthLabel()})`}
          name='lotDepth'
          number
          value={property.lotDepth}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          label='Land SF'
          name='landSf'
          number
          value={property.landSf}
          onChange={this.props.onChange}
        />
        { property.propertySubtypeId == 301 && /* Manufacturing Only */
          <HorizontalForm.TextInput
            label='Min Yard (Acres)'
            name='minYard'
            number
            value={property.minYard}
            onChange={this.props.onChange}
          />
        }
        <HorizontalForm.TextInput
          name='businessPark'
          value={property.businessPark}
          onChange={this.props.onChange}
        />
        <HorizontalForm.Select
          name='cornerProperty'
          options={yesNoOptions}
          value={property.cornerProperty || ""}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='trafficCount'
          number
          value={property.trafficCount}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='trafficCountStreet'
          value={property.trafficCountStreet}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          label={`Traffic Count Frontage (${this.measurementLengthLabel()})`}
          name='trafficCountFrontage'
          number
          value={property.trafficCountFrontage}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextArea
          name='amenities'
          value={property.amenities || ""}
          onChange={this.props.onChange}
        />
        <HorizontalForm.Select
          name='waterfront'
          options={yesNoOptions}
          value={property.waterfront || ""}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          label='Thomas Guide Page #'
          name='thomasGuidePageNumber'
          number
          value={property.thomasGuidePageNumber}
          onChange={this.props.onChange}
        />
        <HorizontalForm.Select
          name='power'
          options={yesNoOptions}
          value={property.power || ""}
          onChange={this.props.onChange}
        />
        { property.propertySubtypeId == 301 && /* Manufacturing Only */
          <HorizontalForm.TextInput
            label='Min Power (Amps)'
            name='minPower'
            number
            value={property.minPower}
            onChange={this.props.onChange}
          />
        }
        <HorizontalForm.TextInput
          name='powerDescription'
          value={property.powerDescription}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='phase'
          value={property.phase}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='voltage'
          value={property.voltage}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='amperage'
          value={property.amperage}
          onChange={this.props.onChange}
        />
        <HorizontalForm.Select
          name='railAccess'
          options={yesNoOptions}
          value={property.railAccess || ""}
          onChange={this.props.onChange}
        />
        <HorizontalForm.Select
          label='Gas / Propane'
          name='gas'
          options={yesNoOptions}
          value={property.gas || ""}
          onChange={this.props.onChange}
        />
        <HorizontalForm.TextInput
          name='gasDescription'
          value={property.gasDescription}
          onChange={this.props.onChange}
        />
      </div>
    );
  }

  render() {
    const property = mergeWith(defaultProperty, this.props.property);
    const typeOptions = propertyTypeSubtypeMap.map(type => [type.name, type.id]);
    const subtypeOptions = this.getPropertySubtypes(property.propertyTypeId).map(subtype => [subtype.name, subtype.id]);

    return (
      <React.Fragment>
        <h6 className="mb-2">Property Information</h6>
        <div className="form-vertical-field-container">
          <Row>
            <Col xs={8}>
              <HorizontalForm.Select
                label='Property Type'
                name='propertyTypeId'
                options={typeOptions}
                value={property.propertyTypeId.toString()}
                onChange={this.handlePropertyTypeIdChange}
              />
            </Col>
            <Col xs={4}>
              <HorizontalForm.Select
                label='Property Subtype'
                name='propertySubtypeId'
                noLabel
                options={subtypeOptions}
                value={property.propertySubtypeId.toString()}
                onChange={this.props.onChange}
              />
            </Col>
          </Row>
          <HorizontalForm.TextInput
            label='Property Name'
            name='name'
            value={property.name}
            onChange={this.props.onChange}
          />
          <HorizontalForm.Select
            includeBlank
            label='Property Use'
            name='propertyUseId'
            options={propertyUseIdOptions}
            value={property.propertyUseId}
            onChange={this.props.onChange}
          />
          <HorizontalForm.TextInput
            name='zoning'
            value={property.zoning}
            onChange={this.props.onChange}
          />
          <Row>
            <Col xs={9}>
              <HorizontalForm.TextInput
                label='Lot Size'
                name='lotSize'
                number
                value={property.lotSize}
                onChange={this.props.onChange}
              />
            </Col>
            <Col className="d-flex gap-4">
              <Form.Check
                checked={property.lotSizeUnits == 'sf'}
                id="lotSizeSf"
                label="SF"
                name='lotSizeUnits'
                type="radio"
                value='sf'
                onChange={this.props.onChange}
              />
              <Form.Check
                checked={property.lotSizeUnits == 'acres'}
                id="lotSizeAcres"
                name='lotSizeUnits'
                label="Acres"
                type="radio"
                value='acres'
                onChange={this.props.onChange}
              />
            </Col>
          </Row>
          <HorizontalForm.TextInput
            label='APN#'
            name='apn'
            value={property.apn}
            onChange={this.props.onChange}
          />
          <HorizontalForm.TextInput
            name='mortgageAmount'
            number
            value={property.mortgageAmount}
            onChange={this.props.onChange}
          />
          <HorizontalForm.DatePicker
            name='mortgageRecordedDate'
            value={property.mortgageRecordedDate}
            onChange={this.handleDateChange}
          />
          <HorizontalForm.TextInput
            name='mortgageSignatoryName'
            value={property.mortgageSignatoryName}
            onChange={this.props.onChange}
          />
          <div
            className='btn btn-light'
            name='property-info-toggle'
            onClick={this.toggleExpand}
          >
            {`${this.state.expanded ? "Hide" : "Show"} Additional Fields`}
          </div>
        </div>
        {this.renderAdditionalFields()}
      </React.Fragment>
    );
  }
}
